
import Vue from "vue/dist/vue.esm.js"
import { VCard, VCardTitle, VCardText, VCardActions, VBtn, VTextField,
        VTextarea, VSpacer, VCol, VRow, VCheckbox, VContainer, VSwitch,
        VForm, VIcon, VDataTable, VChip, VDivider, VSelect, VAlert, VSimpleTable,
        VTabs, VTab, VTabsItems, VTabItem, VFileInput, VProgressCircular, VTooltip, VDialog} from 'vuetify/lib'
import i18n from '@/i18n'
import FileUploader from '@/components/dynamic_factory/FileUploader.vue'
// import { marked } from 'marked'

// Simple markdown renderer component
// const MarkdownView = Vue.extend({
//   props: {
//     content: { type: String, default: '' }
//   },
//   computed: {
//     html() {
//       return this.content ? marked(this.content) : ''
//     }
//   },
//   template: '<div class="markdown-content" v-html="html"></div>'
// })

// Atlassian markup parser (Jira/Confluence style)
function parseAtlassianMarkup(text: string): string {
  if (!text) return ''
  let html = text
    // Escape HTML first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Remove empty {color:x}{color} tags
    .replace(/\{color:[^}]+\}\{color\}/g, '')
    // {color:#hex}text{color} or {color:name}text{color} → <span style="color:...">text</span>
    .replace(/\{color:([^}]+)\}([\s\S]*?)\{color\}/g, '<span style="color:$1">$2</span>')
    // [text|url] → <a href="url">text</a>
    .replace(/\[([^\]|]+)\|([^\]]+)\]/g, '<a href="$2">$1</a>')
    // <tel:xxx> or <mailto:xxx> → remove (already handled in links above)
    .replace(/&lt;tel:[^&]+&gt;/g, '')
    .replace(/&lt;mailto:[^&]+&gt;/g, '')
    // *bold* → <strong>bold</strong>
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    // _italic_ → <em>italic</em>
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // +underline+ → <u>underline</u>
    .replace(/\+([^+]+)\+/g, '<u>$1</u>')
    // Newlines → <br>
    .replace(/\n/g, '<br>')
  return html
}

const AtlassianMarkup = Vue.extend({
  props: {
    content: { type: String, default: '' }
  },
  computed: {
    html() {
      return parseAtlassianMarkup(this.content)
    }
  },
  template: '<div class="atlassian-markup" v-html="html"></div>'
})

// Simple PDF embed component for displaying PDFs from URLs
const PDFEmbed = Vue.extend({
  props: {
    src: { type: String, required: true },
    height: { type: String, default: '600px' },
    width: { type: String, default: '100%' }
  },
  template: `<iframe :src="src" :width="width" :height="height" style="border: none;" type="application/pdf"></iframe>`
})

// Cache compiled templates
const compiledCache = {}

export function getEditorDynamicComponent(templateString, optionsString = null) {
    // Generate cache key - handle both string and object options
    const optionsKey = optionsString
      ? (typeof optionsString === 'string' ? optionsString : JSON.stringify(Object.keys(optionsString)))
      : 'default'
    const cacheKey = templateString + '_v2_' + optionsKey

    if (!compiledCache[cacheKey]) {
      const compiled = Vue.compile(templateString)

      // Parse dynamic options if provided
      let dynamicMethods = {}
      let dynamicComputed = {}
      let dynamicData = () => ({})
      let dynamicMounted = null
      let dynamicWatch = {}

      if (optionsString) {
        try {
          let options
          // Support both string (from DB/API) and object (from direct import)
          if (typeof optionsString === 'string') {
            // Create a function that returns the options object
            // We use Function constructor to evaluate the string in a controlled way
            const optionsFn = new Function('return ' + optionsString)
            options = optionsFn()
          } else {
            // Already an object, use directly
            options = optionsString
          }

          if (options.methods) dynamicMethods = options.methods
          if (options.computed) dynamicComputed = options.computed
          if (options.data) dynamicData = options.data
          if (options.mounted) dynamicMounted = options.mounted
          if (options.watch) dynamicWatch = options.watch
        } catch (e) {
          console.error('Error parsing dynamic options:', e)
        }
      }

      compiledCache[cacheKey] = {
        render: compiled.render,
        staticRenderFns: compiled.staticRenderFns,
        i18n,

        // Inject functions from parent (DynamicComponentRenderer)
        inject: {
          uploadFile: { default: null },
          run_action_2: { default: null },
          getShowNext: { default: () => false },
          getShowPrev: { default: () => false },
        },

        // declare payload as a prop
        props: ["value"],

        data() {
          // Merge dynamic data with default data
          const dynamicDataObj = typeof dynamicData === 'function' ? dynamicData.call(this) : {}
          return {
            localData: { ...this.value },
            ...dynamicDataObj
          }
        },
        computed: {
          // Navigation availability - computed from injected getters for reactivity
          showNext() {
            return this.getShowNext()
          },
          showPrev() {
            return this.getShowPrev()
          },
          // Merge dynamic computed properties
          ...dynamicComputed
        },
        methods: {
          update() {
            // emit back to parent
            this.$emit("input", this.localData)
          },
          run_action(action_name){
            console.log('action called:', action_name)
            this.$emit("run_action", action_name)
          },
          // Merge dynamic methods
          ...dynamicMethods
        },
        watch: {
          ...dynamicWatch
        },
        async mounted() {
          if (dynamicMounted) {
            await dynamicMounted.call(this)
          }
        },
        components: {
          VCard, VCardTitle, VCardText, VCardActions,VSwitch,VForm,
          VBtn, VTextField, VTextarea, VSpacer, VCol, VRow,VCheckbox, VContainer,
          VIcon, VDataTable, VChip, VSelect, VAlert, VSimpleTable, VDivider,
          VTabs, VTab, VTabsItems, VTabItem, PDFEmbed, /* MarkdownView, */ AtlassianMarkup,
          VFileInput, VProgressCircular, VTooltip, VDialog, FileUploader
        }
      }
    }
    return compiledCache[cacheKey]
  }

/**
 * Creates a dynamic component for invoice/facturae viewing
 * This allows AI-customizable invoice templates
 *
 * @param templateString - The Vue template string for the invoice layout
 * @param optionsString - Optional JavaScript object string containing methods, computed, data
 * @returns Vue component definition
 */
export function getInvoiceViewerDynamicComponent(templateString: string, optionsString: string | null = null) {
    const cacheKey = templateString + '_invoice_' + (optionsString || 'default')

    if (!compiledCache[cacheKey]) {
      const compiled = Vue.compile(templateString)

      // Parse dynamic options if provided
      let dynamicMethods = {}
      let dynamicComputed = {}
      let dynamicData = () => ({})

      if (optionsString) {
        try {
          const optionsFn = new Function('return ' + optionsString)
          const options = optionsFn()

          if (options.methods) dynamicMethods = options.methods
          if (options.computed) dynamicComputed = options.computed
          if (options.data) dynamicData = options.data
        } catch (e) {
          console.error('Error parsing dynamic invoice options:', e)
        }
      }

      compiledCache[cacheKey] = {
        render: compiled.render,
        staticRenderFns: compiled.staticRenderFns,

        props: {
          page: {
            type: Object,
            required: true
          },
          num_page: {
            type: Number,
            required: true
          },
          total_pages: {
            type: Number,
            required: true
          }
        },

        data() {
          // Merge dynamic data with invoice-specific data
          const dynamicDataObj = typeof dynamicData === 'function' ? dynamicData.call(this) : {}
          return {
            invoiceable: this.page.invoiceable,
            configuration: this.page.configuration,
            rectified_inv: this.page.rectified_inv,
            total_lines: 0,
            ...dynamicDataObj
          }
        },

        mounted() {
          this.invoiceable.compute()
          this.total_lines = this.invoiceable.get_lines_with_additional_descriptions().length
        },

        watch: {
          computed_lines(newLines: any[]) {
            this.total_lines = newLines.length
          }
        },

        computed: {
          // Standard invoice computed properties
          proformaHeader(): any | null {
            if (!((this.invoiceable.type === 'invoice' && this.invoiceable.related_invoiceables_type === 'proforma'))) {
              return null
            }
            const linea = this.computed_lines.find((item: any) =>
              item.additional_information &&
              item.additional_information.sender_info &&
              (item.additional_information.sender_info.budget_number ||
               item.additional_information.sender_info.budget_date)
            )
            return linea
              ? {
                  budget_number: linea.additional_information.sender_info.budget_number,
                  budget_date: linea.additional_information.sender_info.budget_date,
                  record_reference: linea.additional_information.record_reference,
                  record_reference_date: linea.additional_information.record_reference_date
                }
              : null
          },

          presupuestoHeader(): any | null {
            if (!((this.invoiceable.type === 'invoice' && this.invoiceable.related_invoiceables_type === 'budget'))) {
              return null
            }
            const linea = this.computed_lines.find((item: any) =>
              item.additional_information &&
              item.additional_information.sender_info &&
              (item.additional_information.sender_info.budget_number ||
               item.additional_information.sender_info.budget_date)
            )
            return linea
              ? {
                  budget_number: linea.additional_information.sender_info.budget_number,
                  budget_date: linea.additional_information.sender_info.budget_date,
                  record_reference: linea.additional_information.record_reference,
                  record_reference_date: linea.additional_information.record_reference_date
                }
              : null
          },

          computed_lines(): any[] {
            return this.invoiceable.get_lines_with_additional_descriptions()
          },

          tax_type_to_show(): string {
            return this.invoiceable.tax_type === 'igic' ? 'IGIC' : 'IVA'
          },

          im_suplido_only_invoice(): boolean {
            const isReimbursableExpense =
              (Array.isArray(this.invoiceable.reimbursable_amounts) &&
               this.invoiceable.reimbursable_amounts.length > 0) ||
               Boolean(this.invoiceable.reimbursable_expense_concept)
            const hasLines = this.computed_lines.length > 0
            return isReimbursableExpense && !hasLines
          },

          pageIncludesAdditionalLine(): boolean {
            return this.page.lastPrintedLine >= this.total_lines + 1 && this.page.firstPrintedLine < this.total_lines + 1
          },

          pageIsTotals(): boolean {
            return this.page.lastPrintedLine >= this.total_lines && this.page.firstPrintedLine < this.total_lines
          },

          isLastPage(): boolean {
            return this.page.pageNum === this.total_pages - 1
          },

          showAdditionalInfo(): boolean {
            if (!this.invoiceable.additional_info) return false
            return this.isLastPage || this.pageIsTotals
          },

          showPaymentMessage(): boolean {
            return this.isLastPage || this.pageIsTotals
          },

          showFooter(): boolean {
            return Boolean(this.configuration && this.configuration.footer) && (this.isLastPage || this.pageIsTotals)
          },

          showFooterSmall(): boolean {
            return Boolean(this.configuration && this.configuration.footer_small) && (this.isLastPage || this.pageIsTotals)
          },

          // Merge dynamic computed properties
          ...dynamicComputed
        },

        methods: {
          formatCurrency(value) {
            if (value === null || value === undefined) return '0,00 €';

            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            const fixed = numValue.toFixed(2);
            const [integer, decimal] = fixed.split('.');

            const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

            return `${formattedInteger},${decimal} €`;
          },

          payment_message(payment_means) {
            // Import getPaymentMeansMessage from domain if needed
            const paymentMap = {
              '01': 'Pago al contado',
              '02': 'Pago mediante recibo domiciliado',
              '03': 'Pago mediante recibo',
              '04': 'Pago mediante transferencia',
              '05': 'Pago mediante letra aceptada',
              '06': 'Pago mediante crédito documentario',
              '07': 'Pago mediante contrato adjudicación',
              '08': 'Pago mediante letra de cambio',
              '09': 'Pago mediante pagaré',
              '10': 'Pago mediante pagaré notarial',
              '11': 'Pago mediante cheque',
              '12': 'Pago mediante reembolso',
              '13': 'Pago mediante especiales',
              '14': 'Pago mediante compensación',
              '15': 'Pago mediante giro postal',
              '16': 'Pago mediante certificación de deuda',
              '17': 'Pago mediante tarjeta',
            };
            return paymentMap[payment_means] || 'Forma de pago no especificada';
          },

          get_total_amount(invoice_line) {
            let gross = invoice_line.gross_amount;
            if (invoice_line.discounts.length > 0 && invoice_line.discounts[0].rate > 0)
              gross -= gross * invoice_line.discounts[0].rate / 100;
            let tax_amount = gross * invoice_line.taxes[0].rate;
            let recargo = 0;
            if (invoice_line.taxes[0].recargo_amount) {
              recargo = invoice_line.taxes[0].recargo_amount;
            }
            let withheld_amount = 0;
            if (invoice_line.taxes_withheld.length) {
              withheld_amount = invoice_line.taxes_withheld[0].amount
            }
            return gross + tax_amount + recargo - withheld_amount;
          },

          formatInvoiceDate(date) {
            if (!date) return '';
            const [year, month, day] = date.split('-');
            return `${day}/${month}/${year}`;
          },

          formatField(field) {
            const fieldMap = {
              0.21: '21%',
              0.10: '10%',
              0.04: '4%',
              0.00: '0%',
              0.005: '4% RE 0.5%',
              0.014: '10% RE 1.4%',
              0.052: '21% RE 5.2%',
              na: '-',
              0.07: '7%',
              0.15: '15%',
              0.19: '19%',
            };

            return fieldMap[field] || field;
          },

          // Merge dynamic methods
          ...dynamicMethods
        },

        components: {
          VCard, VCardTitle, VCardText, VCardActions, VSwitch, VForm,
          VBtn, VTextField, VTextarea, VSpacer, VCol, VRow, VCheckbox, VContainer,
          VIcon, VDataTable, VChip, VDivider, VSelect, VAlert, VSimpleTable,
          VTabs, VTab, VTabsItems, VTabItem, PDFEmbed
        }
      }
    }
    return compiledCache[cacheKey]
  }